import React, { useState, useEffect, useRef } from 'react';
import { FiX } from 'react-icons/fi';
import api from '../../services/api';
import Alert from '../Common/Alert';
import { ElectronicSign } from '../Common/ElectronicSign';
import MultiSelectDropdown from '../Common/MultiSelectDropdown';
import { sites as siteMessages, common as commonMessages } from '../../utils/validationMessages';
import VisitModalHeader from '../Visits/VisitModalHeader';
import './AssignCRAModal.css';

const AssignCRAModal = ({ site, projectCode: projectCodeProp, onClose, onAssign }) => {
  const [cras, setCras] = useState([]);
  const [selectedCRAs, setSelectedCRAs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [craError, setCraError] = useState('');
  const [showESignModal, setShowESignModal] = useState(false);
  const [toast, setToast] = useState(null);
  const eSignRemarksRef = useRef('');

  const getCraDisplayName = (cra) => {
    const fromApi = (cra.name ?? cra.Name ?? '').toString().trim();
    if (fromApi) return fromApi;

    const firstName = (cra.firstName ?? cra.FirstName ?? '').toString().trim();
    const lastName = (cra.lastName ?? cra.LastName ?? '').toString().trim();
    const userName = (cra.userName ?? cra.UserName ?? '').toString().trim();
    const fullName = [firstName, lastName].filter(Boolean).join(' ').trim();

    if (fullName && userName) return `${fullName} (${userName})(CRA)`;
    if (fullName) return `${fullName}(CRA)`;
    if (userName) return `${userName}(CRA)`;
    return '';
  };

  useEffect(() => {
    fetchCRAs();
  }, []);

  // When site has craList (e.g. from feasibility list or after fetch), pre-populate selected CRAs
  useEffect(() => {
    if (!site?.craList?.length) return;
    const ids = site.craList.map(c => String(c.id));
    setSelectedCRAs(prev => (prev.length === 0 ? ids : prev));
  }, [site?.siteId, site?.craList]);

  const fetchCRAs = async () => {
    try {
      setLoading(true);
      const response = await api.get('/users/cras');
      const raw = response.data || [];
      const seen = new Set();
      const crasList = raw
        .map(cra => ({
          id: String(cra.id),
          name: cra.name || '',
          firstName: cra.firstName ?? cra.FirstName ?? '',
          lastName: cra.lastName ?? cra.LastName ?? '',
          userName: cra.userName ?? cra.UserName ?? ''
        }))
        .filter(cra => getCraDisplayName(cra) && !seen.has(cra.id) && seen.add(cra.id));
      setCras(crasList);
    } catch (err) {
      console.error('Error fetching CRAs:', err);
      const errorMessage = err.response?.data?.message ||
                          err.response?.data?.error ||
                          err.message ||
                          'Failed to load CRAs';
      setCras([]);
      setToast({ type: 'error', message: errorMessage });
    } finally {
      setLoading(false);
    }
  };

  const handleAssign = () => {
    setCraError('');
    if (!selectedCRAs || selectedCRAs.length === 0) {
      setCraError(siteMessages.assignCRA || 'Select at least one CRA.');
      return;
    }
    eSignRemarksRef.current = '';
    setShowESignModal(true);
  };

  const runAfterESign = () => {
    const remark = (eSignRemarksRef.current != null && typeof eSignRemarksRef.current === 'string')
      ? eSignRemarksRef.current.trim()
      : '';
    setShowESignModal(false);
    setToast({ type: 'success', message: commonMessages.authSuccess });
    onAssign(site.siteId, selectedCRAs, remark);
  };

  return (
    <>
    <div className="modal-overlay assign-cra-overlay">
      <div className="assign-cra-modal-wrapper" onClick={(e) => e.stopPropagation()}>
        <div className="assign-cra-modal">
          <div className="assign-cra-header">
            <h3>Assign CRA</h3>
            <button className="assign-cra-close" onClick={onClose}>
              <FiX size={24} />
            </button>
          </div>
          <div className="assign-cra-body">
            {site && (
              <VisitModalHeader
                projectCode={site.projectCode ?? site.project ?? projectCodeProp}
                siteName={site.siteName ?? site.sitedescription}
                siteCode={site.siteCode ?? site.vSiteCode}
                piName={site.piName ?? site.investigator ?? site.investigatorDesc}
              />
            )}
            <div className="form-group">
              <label className="form-label">CRA <span className="required-asterisk">*</span></label>
              {loading ? (
                <div>Loading CRA...</div>
              ) : (
                <>
                  <MultiSelectDropdown
                    placeholder="Select CRA"
                    options={cras.map(c => ({ value: c.id, label: getCraDisplayName(c) }))}
                    selectedValues={selectedCRAs}
                    onChange={setSelectedCRAs}
                    disabled={loading}
                  />
                  {craError && (
                    <div className="field-error" style={{ color: '#c00', fontSize: '12px', marginTop: '4px' }}>{craError}</div>
                  )}
                </>
              )}
            </div>
          </div>
          <div className="assign-cra-footer">
            <button className="btn btn-primary" onClick={handleAssign} disabled={loading}>
              Save
            </button>
            <button className="btn btn-secondary" onClick={onClose}>
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
    {showESignModal && (
      <ElectronicSign
        show={true}
        onClose={() => { setShowESignModal(false); eSignRemarksRef.current = ''; }}
        onAuthSuccess={(vRemarks) => { eSignRemarksRef.current = vRemarks || ''; }}
        onSave={runAfterESign}
      />
    )}
    {toast && (
      <div style={{ position: 'fixed', top: 16, right: 16, zIndex: 10000 }}>
        <Alert type={toast.type} message={toast.message} onClose={() => setToast(null)} />
      </div>
    )}
    </>
  );
};

export default AssignCRAModal;
